/**
 * Member mutations: invite a member, add a membership.
 *
 * NON-OPTIMISTIC: these grant access (RBAC/ABAC) — wait for the backend, then
 * refresh the members list.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type InviteMemberResponse,
  inviteMemberResponseSchema,
  type MembershipRow,
  membershipRowSchema,
} from '../responses';
import type { AddMembershipBody, CreateMemberBody } from './_types';

/** `POST /members` — invite a member (omit hotelId for a tenant-wide grant). */
export function useInviteMember(
  tenantId: string,
): UseMutationResult<InviteMemberResponse, unknown, CreateMemberBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateMemberBody) => {
      const result = await client.POST('/api/v1/tenants/{tenantId}/members', {
        params: { path: { tenantId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, inviteMemberResponseSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.members(tenantId) });
    },
  });
}

export interface AddMembershipVars {
  userId: string;
  body: AddMembershipBody;
}

/** `POST /members/:userId/memberships` — grant an existing user another membership. */
export function useAddMembership(
  tenantId: string,
): UseMutationResult<MembershipRow, unknown, AddMembershipVars> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, body }: AddMembershipVars) => {
      const result = await client.POST('/api/v1/tenants/{tenantId}/members/{userId}/memberships', {
        params: { path: { tenantId, userId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, membershipRowSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.members(tenantId) });
    },
  });
}
